'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, ChevronRight, Star } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { getFeaturedStoreItems } from '@/lib/thegame/store-client';

/**
 * Featured Products Component
 * Displays top products in a premium carousel/grid layout
 * Uses M2M bridge to fetch inventory
 */
interface FeaturedProductsProps {
  title?: string;
  subtitle?: string;
  layout?: 'carousel' | 'grid' | 'masonry' | 'list';
  autoPlay?: boolean;
}

interface Product {
  id: string;
  name: string;
  price?: number;
  type?: string;
  subItemType?: string;
  collection?: string;
  media?: {
    main?: string;
    thumb?: string;
  };
}

export default function FeaturedProducts({
  title = 'Featured Creations',
  subtitle = 'Hand-picked digital masterpieces',
  layout = 'grid',
  autoPlay = false,
}: FeaturedProductsProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(layout === 'list' ? 'list' : 'grid');

  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadProducts = async () => {
      try {
        // Only show loading on the first attempt to avoid flickering
        if (retryCount === 0) {
          setLoading(true);
        }
        
        const result = await getFeaturedStoreItems();
        if (cancelled) return;

        if (result.success && Array.isArray(result.items) && result.items.length > 0) {
          const forSaleItems = result.items
            .filter((item: any) => item.status === 'for-sale')
            .map((item: any) => {
              let price = item.price;
              if (item.pricing?.targetPrice?.minorUnits) {
                price = Number(item.pricing.targetPrice.minorUnits) / 100;
              } else if (item.pricing?.unitCost?.minorUnits) {
                price = Number(item.pricing.unitCost.minorUnits) / 100;
              } else if (typeof price === 'string') {
                price = Number(price);
              } else if (typeof price === 'object' && price !== null && 'minorUnits' in price) {
                price = Number(price.minorUnits) / 100;
              }

              if (typeof price !== 'number' || isNaN(price)) {
                price = 0;
              }
              return {
                ...item,
                price
              };
            });
          setProducts(forSaleItems);
          setLoading(false);
        } else {
          // If it failed or returned nothing, and we haven't exhausted retries
          if (retryCount < 2) {
            const delay = 2000 * (retryCount + 1);
            console.warn(`[FeaturedProducts] Load attempt ${retryCount + 1} failed or empty. Retrying in ${delay}ms...`);
            
            setTimeout(() => {
              if (!cancelled) {
                setRetryCount(prev => prev + 1);
              }
            }, delay);
          } else {
            // Final attempt failed
            setProducts([]);
            setLoading(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load products:', error);
          if (retryCount < 2) {
            setRetryCount(prev => prev + 1);
          } else {
            setProducts([]);
            setLoading(false);
          }
        }
      }
    };

    void loadProducts();
    return () => {
      cancelled = true;
    };
  }, [retryCount]);
  const productContainerClasses =
    viewMode === 'grid'
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
      : 'grid-cols-1';

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-12">
        <div>
          <h2 className="text-4xl font-bold text-foreground mb-2">{title}</h2>
          {subtitle && <p className="text-muted-foreground text-lg">{subtitle}</p>}
        </div>

        {/* Layout Toggle */}
        <div className="flex gap-2">
          {(['grid', 'list'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === mode ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="space-y-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-48 bg-gray-200 rounded-lg animate-pulse"></div>
              <div className="mt-4 space-y-3">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-3 bg-gray-300 rounded"></div>
                <div className="h-6 bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Products Display */}
      {!loading && products.length > 0 && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={`grid gap-6 ${productContainerClasses}`}
          >
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className={`group relative bg-card rounded-lg border-2 border-border overflow-hidden hover:shadow-xl transition-all ${
                  viewMode === 'list' ? 'sm:flex sm:items-stretch' : ''
                }`}
              >
                {/* Badge */}
                <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider z-10">
                  Featured
                </div>

                {/* Product Image */}
                <div
                  className={`relative bg-muted/30 dark:bg-gray-900/50 overflow-hidden ${
                    viewMode === 'list' ? 'sm:w-1/2 sm:min-h-[220px]' : 'aspect-square'
                  }`}
                >
                  {product.media?.main ? (
                    <div className={`relative w-full h-full ${viewMode === 'list' ? 'aspect-square sm:aspect-[16/9]' : ''}`}>
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <Image
                        src={product.media.main}
                        alt={product.name}
                        fill
                        className="object-contain w-full h-full p-4"
                        sizes={viewMode === 'list' ? '(max-width: 768px) 100vw, 50vw' : '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'}
                        priority={index < 2}
                      />
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted/30">
                      <Star className="w-12 h-12 text-muted-foreground opacity-20" />
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-1">
                    {product.name}
                  </h3>
                  
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="text-[10px] uppercase tracking-wider bg-muted px-2 py-0.5 rounded-md font-medium text-muted-foreground">
                      {product.type}
                    </span>
                    {product.subItemType && (
                      <span className="text-[10px] uppercase tracking-wider bg-muted px-2 py-0.5 rounded-md font-medium text-muted-foreground">
                        {product.subItemType}
                      </span>
                    )}
                  </div>

                  {/* Price and CTA */}
                  <div className="mt-auto flex items-center justify-between gap-4">
                    {product.type === 'artwork' ? (
                      <div className="text-sm font-bold text-muted-foreground whitespace-nowrap italic">
                        Make an offer
                      </div>
                    ) : product.price !== undefined ? (
                      <div className="text-2xl font-bold text-primary">
                        ${Number(product.price || 0).toFixed(2)}
                      </div>
                    ) : null}

                    <Link
                      href={`/store/${product.id}`}
                      className="group/btn inline-flex items-center gap-2 px-4 py-2 bg-transparent border border-border rounded-lg hover:bg-muted/50 transition-all duration-300"
                    >
                      <ShoppingCart className="w-4 h-4 text-cyan-500 group-hover/btn:text-pink-500 transition-colors" />
                      <span className="bg-gradient-to-r from-cyan-500 to-pink-500 bg-clip-text text-transparent font-bold text-sm">
                        Details
                      </span>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </>
      )}

      {!loading && products.length === 0 && (
        <div className="text-center py-20">
          <div className="mb-8">
            <Star className="w-16 h-16 mx-auto text-primary opacity-20" />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-4">
            No Featured Products Yet
          </h3>
          <p className="text-muted-foreground text-lg mb-8">
            The collection is being curated. Check back soon for exclusive digital creations.
          </p>
          <Link
            href="/portfolio"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground font-bold rounded-lg hover:bg-primary/90 transition-all"
          >
            Browse Full Portfolio
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      )}
    </section>
  );
}
